import * as React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from "lucide-react";
import { cn, getPageNumbers } from "@/lib/utils";
import { Button } from "./Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

export interface PaginationProps {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Total number of items (for results count) */
  totalItems?: number;
  /** Items per page */
  itemsPerPage?: number;
  /** Callback when page size changes */
  onPageSizeChange?: (size: number) => void;
  /** Available page size options */
  pageSizeOptions?: number[];
  /** Show results count (e.g., "Showing 1-10 of 100") */
  showResultsCount?: boolean;
  /** Show first/last page buttons (hidden on mobile regardless) */
  showFirstLast?: boolean;
  /** Custom labels for buttons */
  labels?: {
    previous?: string;
    next?: string;
    first?: string;
    last?: string;
    resultsOf?: string;
    showing?: string;
    to?: string;
    rowsPerPage?: string;
    pageOf?: string;
  };
  /** Additional class name */
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage = 10,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 40, 50],
  showResultsCount = true,
  showFirstLast = true,
  labels = {},
  className,
}) => {
  const [localItemsPerPage, setLocalItemsPerPage] =
    React.useState(itemsPerPage);

  React.useEffect(() => {
    setLocalItemsPerPage(itemsPerPage);
  }, [itemsPerPage]);

  const {
    previous = "Previous",
    next = "Next",
    first = "First",
    last = "Last",
    resultsOf = "of",
    showing = "Showing",
    to = "to",
    rowsPerPage = "Rows per page",
    pageOf = "of",
  } = labels;

  const startResult = totalItems
    ? (currentPage - 1) * localItemsPerPage + 1
    : 0;
  const endResult = totalItems
    ? Math.min(currentPage * localItemsPerPage, totalItems)
    : 0;

  const handlePrevious = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  const handleFirst = () => onPageChange(1);
  const handleLast = () => onPageChange(totalPages);
  const handlePageSizeChange = (value: string) => {
    const nextSize = Number(value);
    if (!Number.isFinite(nextSize)) return;

    setLocalItemsPerPage(nextSize);
    onPageSizeChange?.(nextSize);
    if (currentPage !== 1) onPageChange(1);
  };
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  if (totalPages <= 1) {
    return (
      <div className={cn("flex items-center justify-between py-3", className)}>
        <div className="flex items-center gap-4">
          {showResultsCount && totalItems !== undefined && (
            <p className="text-sm text-muted-foreground">
              {showing} {startResult} {to} {endResult} {resultsOf} {totalItems}
            </p>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {rowsPerPage}
            </span>
            <Select
              value={String(localItemsPerPage)}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(size => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <nav
      className={cn(
        "flex flex-col sm:flex-row items-center justify-between gap-4 py-3",
        className,
      )}
      aria-label="Pagination"
    >
      {/* Left side: Results count + Page size selector */}
      <div className="flex items-center gap-4 order-2 sm:order-1">
        {showResultsCount && totalItems !== undefined && (
          <p className="text-sm text-muted-foreground">
            {showing} <span className="font-medium">{startResult}</span> {to}{" "}
            <span className="font-medium">{endResult}</span> {resultsOf}{" "}
            <span className="font-medium">{totalItems}</span>
          </p>
        )}

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {rowsPerPage}
          </span>
          <Select
            value={String(localItemsPerPage)}
            onValueChange={handlePageSizeChange}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map(size => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Right side: Navigation controls */}
      <div className="flex items-center gap-1 order-1 sm:order-2">
        {/* First Page Button — hidden on mobile */}
        {showFirstLast && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleFirst}
            disabled={currentPage === 1}
            className="hidden md:inline-flex h-8 w-8 p-0"
            aria-label={first}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Previous Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="h-8 w-8 p-0"
          aria-label={previous}
        >
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
        </Button>

        {/* Page Numbers */}
        <div className="hidden sm:flex items-center gap-1">
          {pageNumbers.map((page, index) => {
            if (page === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="inline-flex items-center justify-center h-8 min-w-8 px-2"
                >
                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                </span>
              );
            }

            return (
              <Button
                key={page}
                variant={page === currentPage ? "primary" : "outline"}
                size="sm"
                onClick={() => onPageChange(page)}
                className="h-8 min-w-8 px-2"
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </Button>
            );
          })}
        </div>

        {/* Mobile Page Indicator */}
        <span className="sm:hidden text-sm text-muted-foreground px-2 whitespace-nowrap">
          Page {currentPage} {pageOf} {totalPages}
        </span>

        {/* Next Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className="h-8 w-8 p-0"
          aria-label={next}
        >
          <ChevronRight className="h-4 w-4 rtl:rotate-180" />
        </Button>

        {/* Last Page Button — hidden on mobile */}
        {showFirstLast && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleLast}
            disabled={currentPage === totalPages}
            className="hidden md:inline-flex h-8 w-8 p-0"
            aria-label={last}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </nav>
  );
};

Pagination.displayName = "Pagination";
